/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react'
import { KTIcon, toAbsoluteUrl } from '../../../helpers'
import { Appointment } from '../../../../app/pages/appointments/components/AppointmentsList'
import { useQuery } from 'react-query'
import { get_upcoming_appoitnments } from '../../../../app/pages/dashboard/DashboardApi'
import { formatDateWithTime, formatDynamicDateTime, formatDynamicDateOnly } from '../../../../app/helper-functions/DateFunction'
import { useNavigate } from 'react-router-dom'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { useGeneralSettings } from '../../../../app/contexts/GeneralSettingsContext'
// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

type Props = {
  className: string
  is_people?: boolean
}

const TablesWidget3: React.FC<Props> = ({ className, is_people }) => {
  const { getSettingValue } = useGeneralSettings();
  const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
  const userTimeFormat = getSettingValue('time_format') || '12hr';
  const userTimeZone = getSettingValue('time_zone') || 'UTC';
  
  const [pastAppointments, setpastAppointments] = useState<any[]>([])
  const [chartData, setChartData] = useState<any>(null)
  const navigate = useNavigate()

  const { data: appointmentsData } = useQuery(['past', { is_people }], () =>
    get_upcoming_appoitnments(['past'], is_people)
  )

  useEffect(() => {
    if (appointmentsData?.data?.past) {
      setpastAppointments(appointmentsData.data.past)
      prepareChartData(appointmentsData.data.past)
    }
  }, [appointmentsData])

  const prepareChartData = (appointments: Appointment[]) => {
    // Group appointments by date
    const dateCounts: Record<string, number> = {}

    appointments.forEach(appointment => {
      appointment.appointment_date_time_slots.forEach(slot => {
        const rawDate = slot.datetime.split(' ')[0] // Get just the date part
        const formattedDate = formatDynamicDateOnly(rawDate, userDateFormat, userTimeZone)
        dateCounts[formattedDate] = (dateCounts[formattedDate] || 0) + 1
      })
    })

    const dates = Object.keys(dateCounts)
    const counts = Object.values(dateCounts)

    setChartData({
      labels: dates,
      datasets: [
        {
          label: 'Number of Appointments',
          data: counts,
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    })
  }

  const handleAppointmentView = (appointment) => {
    navigate("/appointments/view-appointment", { state: { id: appointment.id } })
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Past Appointments by Date',
      },
    },
  }

  return (
    <div className={`row ${className}`}>

      {/* Chart Card */}
      <div className="col-md-12 mb-5">
        <div className="card">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">Appointments Chart</span>
            </h3>
          </div>
          {chartData ? (
            <div className="card-body">
              <div style={{ height: '300px', marginBottom: '20px' }}>
                <Bar options={chartOptions} data={chartData} />
              </div>
            </div>
          ) : (
            <div className="card-body text-center py-10">
              <KTIcon iconName="chart" className="fs-2tx text-muted mb-5" />
              <h4 className="text-gray-600">No chart data available</h4>
            </div>
          )}
        </div>
      </div>

      {/* Listing Card */}
      <div className="col-md-12">
        <div className="card">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">Past Appointments</span>
              <span className="text-muted mt-1 fw-semibold fs-7">
                {pastAppointments.length} past appointments
              </span>
            </h3>
          </div>

          <div className="card-body py-3">
            {pastAppointments.length === 0 ? (
              <div className="text-center py-10">
                <KTIcon iconName="clock" className="fs-2tx text-muted mb-5" />
                <h4 className="text-gray-600">No past appointments found</h4>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr className="fw-bold text-muted bg-light">
                      <th className="ps-4 min-w-200px">Client</th>
                      <th className="min-w-150px">Details</th>
                      <th className="min-w-120px">Time Slots</th>
                      <th className="min-w-100px">Status</th>
                      <th className="min-w-100px text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastAppointments.map((appointment, index) => (
                      <tr key={index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-45px me-5">
                              <span className="symbol-label bg-light-primary">
                                <KTIcon iconName="user" className="fs-2 text-primary" />
                              </span>
                            </div>
                            <div className="d-flex flex-column">
                              <a href="#" className="text-dark fw-bold text-hover-primary">
                                {appointment.full_name}
                              </a>
                              <span className="text-muted fw-semibold">{appointment.email}</span>
                              <span className="text-muted">{appointment.phone_number}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <span className="text-dark fw-bold">{appointment?.subcategory_title}</span>
                            <span className="text-muted">Location: {appointment?.location_type.replace(/_/g, ' ')}</span>
                            <span className="text-muted">Amount: {getSettingValue('currency_symbol') ? `${getSettingValue('currency_symbol')} ${appointment?.amount}` : `${appointment?.amount}`}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            {appointment.appointment_date_time_slots.slice(0, 3).map((slot, i) => (
                              <span key={i} className="text-dark fw-semibold mb-1">
                                 {formatDynamicDateTime(slot.datetime, userDateFormat, userTimeFormat, userTimeZone)}
                              </span>
                            ))}
                            {appointment.appointment_date_time_slots.length > 3 && (
                              <span className="badge badge-light-primary">
                                +{appointment.appointment_date_time_slots.length - 3} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            {appointment.appointment_status === 'open' ? (
                              <span className="badge badge-light-success fs-7 fw-bold">
                                {appointment.appointment_status}
                              </span>
                            ) : (
                              <span className="badge badge-light-danger fs-7 fw-bold">
                                {appointment.appointment_status}
                              </span>
                            )}
                            {appointment.payment_status && (
                              <span
                                className={`badge mt-2 ${appointment.payment_status === 'waiting payment'
                                    ? 'badge-light-warning'
                                    : 'badge-light-success'
                                  } fs-7 fw-bold`}
                              >
                                {appointment.payment_status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-end flex-shrink-0">
                            <button
                              onClick={() => handleAppointmentView(appointment)}
                              className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                            >
                              <KTIcon iconName="arrow-right" className="fs-2" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-primary" onClick={() => navigate('/appointments/past-appointments-list')}>
              View All Appointments
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { TablesWidget3 }